import React, { useMemo } from "react";
import {
    Grid,
    Card,
    CardContent,
    Typography,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Button
} from "@mui/material";

import {
    ResponsiveContainer,
    AreaChart,
    Area,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar
} from "recharts";

import LogoutIcon from "@mui/icons-material/Logout";
import SavingsIcon from "@mui/icons-material/Savings";
import { useNavigate } from "react-router-dom";
import { motion } from "motion/react";

const containerVariants = {
    hidden: {},
    visible: {
        transition: {
            staggerChildren: 0.08
        }
    }
};

const itemVariants = {
    hidden: {
        opacity: 0,
        y: 20
    },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.45,
            ease: "easeOut"
        }
    }
};

const COLORS = ["#4caf50", "#ef5350"];

const GraficosSeguimientos = () => {

    const abonos = JSON.parse(sessionStorage.getItem("abonos"));
    const rendimiento = Number(sessionStorage.getItem("rendimiento"));

    const {
        datos,
        progreso,
        pie,
        saldoAhorrado,
        meta,
        pagados,
        pendientes,
        ganancia,
        totalFinal
    } = useMemo(() => {

        let acumulado = 0;

        const datos = abonos.map(item => {

            const cantidad = Number(item.cantidad);

            if (item.ahorro_pagado) {
                acumulado += cantidad;
            }

            return {
                ...item,
                cantidad,
                fecha: new Date(item.fecha_pago).toLocaleDateString("es-MX", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric"
                }),
                acumulado
            };
        });

        const pagados = datos.filter(x => x.ahorro_pagado).length;

        const pendientes = datos.length - pagados;

        const saldoAhorrado = datos
            .filter(x => x.ahorro_pagado)
            .reduce((a, b) => a + b.cantidad, 0);

        const meta = datos.reduce((a, b) => a + b.cantidad, 0);

        const ganancia = meta * rendimiento;
        const totalFinal = meta + ganancia;

        return {
            datos,
            progreso: datos,
            saldoAhorrado,
            meta,
            pagados,
            pendientes,
            ganancia,
            totalFinal,
            pie: [
                {
                    name: "Pagados",
                    value: pagados
                },
                {
                    name: "Pendientes",
                    value: pendientes
                }
            ]
        };

    }, [abonos]);


    const navigation = useNavigate();

    const cerrarSesion = () => {
        sessionStorage.clear();
        navigation("/ahorrador-login");
    };

    const formatNumber = (value) => {
        const [intPart, decimalPart] = value.split('.');

        const formattedInt = parseInt(intPart, 10).toLocaleString('en-US');
        return decimalPart !== undefined ? `${formattedInt}.${decimalPart}` : formattedInt;
    };

    const formatFecha = (value) => {

        const fecha = new Date(value + ' 08:00:00')
        const meses = ['ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO', 'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE']

        return `${fecha.getDate()} DE ${meses[fecha.getMonth()]} DE ${fecha.getFullYear()}`

    }

    return (
        <Box
            component={motion.div}
            initial={{
                opacity: 0,
                y: -40,
            }}
            animate={{
                opacity: 1,
                y: 0,
            }}
            transition={{
                duration: 0.6,
                easing: "ease-out",
            }}
            sx={{
                minHeight: "100vh",
                bgcolor: "#f5f7fb", // color de fondo
                p: 3
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: {
                        xs: "column",
                        sm: "row"
                    },
                    justifyContent: "space-between",
                    alignItems: {
                        xs: "flex-start",
                        sm: "center"
                    },
                    gap: 2,
                    mb: 4
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2
                    }}
                >
                    <SavingsIcon
                        sx={{
                            fontSize: 50,
                            color: "primary.main"
                        }}
                    />

                    <Box>
                        <Typography
                            variant="h4"
                            fontWeight="bold"
                            sx={{
                                fontSize: {
                                    xs: "1.6rem",
                                    md: "2.1rem"
                                }
                            }}
                        >
                            Seguimiento de Ahorro
                        </Typography>

                        <Typography color="text.secondary">
                            Consulta el avance de tus aportaciones.
                        </Typography>
                    </Box>
                </Box>

                <Button
                    component={motion.button}
                    whileHover={{
                        scale: 1.05,
                    }}
                    whileTap={{
                        scale: 0.96,
                    }}
                    variant="contained"
                    color="error"
                    startIcon={<LogoutIcon />}
                    onClick={cerrarSesion}
                    sx={{
                        width: {
                            xs: "100%",
                            sm: "auto"
                        }
                    }}
                >
                    Cerrar sesión
                </Button>
            </Box>

            <Grid container spacing={3}>

                {/* KPIs */}

                <Grid
                    size={{ xs: 12, md: 3 }}
                    component={motion.div}
                    initial={{
                        opacity: 0,
                        y: 40,
                        scale: 0.95,
                    }}
                    animate={{
                        opacity: 1,
                        y: 0,
                        scale: 1,
                    }}
                    transition={{
                        duration: 0.45,
                        delay: 0.15,
                        easing: "ease-out",
                    }}>

                    <Card
                        component={motion.div}
                        whileHover={{
                            y: -6,
                            scale: 1.02,
                        }}
                        transition={{
                            duration: 0.2,
                        }}
                    >

                        <CardContent>

                            <Typography color="text.secondary">
                                Ahorrado
                            </Typography>

                            <Typography variant="h4">

                                ${saldoAhorrado.toLocaleString("en-US")}

                            </Typography>

                        </CardContent>

                    </Card>

                </Grid>

                <Grid
                    size={{ xs: 12, md: 3 }}
                    component={motion.div}
                    initial={{
                        opacity: 0,
                        y: 40,
                        scale: 0.95,
                    }}
                    animate={{
                        opacity: 1,
                        y: 0,
                        scale: 1,
                    }}
                    transition={{
                        duration: 0.45,
                        delay: 0.25,
                        easing: "ease-out",
                    }}>

                    <Card
                        component={motion.div}
                        whileHover={{
                            y: -6,
                            scale: 1.02,
                        }}
                        transition={{
                            duration: 0.2,
                        }}
                    >

                        <CardContent>

                            <Typography color="text.secondary">
                                Meta
                            </Typography>

                            <Typography variant="h4">

                                ${meta.toLocaleString("en-US")}

                            </Typography>

                        </CardContent>

                    </Card>

                </Grid>

                <Grid
                    size={{ xs: 12, md: 3 }}
                    component={motion.div}
                    initial={{
                        opacity: 0,
                        y: 40,
                        scale: 0.95,
                    }}
                    animate={{
                        opacity: 1,
                        y: 0,
                        scale: 1,
                    }}
                    transition={{
                        duration: 0.45,
                        delay: 0.35,
                        easing: "ease-out",
                    }}>

                    <Card
                        component={motion.div}
                        whileHover={{
                            y: -6,
                            scale: 1.02,
                        }}
                        transition={{
                            duration: 0.2,
                        }}>

                        <CardContent>

                            <Typography color="text.secondary">
                                Pagados
                            </Typography>

                            <Typography variant="h4">

                                {pagados}

                            </Typography>

                        </CardContent>

                    </Card>

                </Grid>

                <Grid
                    size={{ xs: 12, md: 3 }}
                    component={motion.div}
                    initial={{
                        opacity: 0,
                        y: 40,
                        scale: 0.95,
                    }}
                    animate={{
                        opacity: 1,
                        y: 0,
                        scale: 1,
                    }}
                    transition={{
                        duration: 0.45,
                        delay: 0.45,
                        easing: "ease-out",
                    }}>

                    <Card
                        component={motion.div}
                        whileHover={{
                            y: -6,
                            scale: 1.02,
                        }}
                        transition={{
                            duration: 0.2,
                        }}>

                        <CardContent>

                            <Typography color="text.secondary">
                                Pendientes
                            </Typography>

                            <Typography variant="h4">

                                {pendientes}

                            </Typography>

                        </CardContent>

                    </Card>

                </Grid>

                <Grid
                    size={12}
                    component={motion.div}
                    initial={{
                        opacity: 0,
                        y: 30,
                    }}
                    animate={{
                        opacity: 1,
                        y: 0,
                    }}
                    transition={{
                        duration: 0.6,
                        delay: 0.55,
                        easing: "ease-out",
                    }}>

                    <Card
                        component={motion.div}
                        whileHover={{
                            y: -6,
                            scale: 1.02,
                        }}
                        transition={{
                            duration: 0.2,
                        }}
                        sx={{
                            background: "linear-gradient(135deg, #2E7D32, #43A047)",
                            color: "white"
                        }}
                    >
                        <CardContent>

                            <Typography variant="h6">
                                💰 Monto estimado al finalizar
                            </Typography>

                            <Typography
                                variant="h3"
                                fontWeight="bold"
                                sx={{ my: 2 }}
                            >
                                ${totalFinal.toLocaleString("en-US")}
                            </Typography>

                            <Typography>
                                Ahorro aportado: <strong>${meta.toLocaleString("en-US")}</strong>
                            </Typography>

                            <Typography>
                                Rendimiento: <strong>${ganancia.toLocaleString("en-US")}</strong>
                            </Typography>

                            <Typography sx={{ mt: 1, opacity: 0.9 }}>
                                Incluye un rendimiento del {(rendimiento * 100).toFixed(0)}%
                            </Typography>

                        </CardContent>

                    </Card>

                </Grid>


                {/* Progreso */}

                <Grid
                    size={{ xs: 12, md: 8 }}
                    component={motion.div}
                    initial={{
                        opacity: 0,
                        x: -50,
                    }}
                    animate={{
                        opacity: 1,
                        x: 0,
                    }}
                    transition={{
                        duration: 0.6,
                        delay: 0.65,
                        easing: "ease-out",
                    }}>

                    <Card
                        component={motion.div}
                        whileHover={{
                            y: -6,
                            scale: 1.02,
                        }}
                        transition={{
                            duration: 0.2,
                        }}>

                        <CardContent>

                            <Typography
                                variant="h6"
                                gutterBottom>

                                Progreso del ahorro

                            </Typography>

                            <ResponsiveContainer
                                width="100%"
                                height={300}>

                                <AreaChart
                                    data={progreso}>

                                    <CartesianGrid strokeDasharray="3 3" />

                                    <XAxis dataKey="fecha" />

                                    <YAxis />

                                    <Tooltip />

                                    <Area
                                        type="monotone"
                                        dataKey="acumulado"
                                        stroke="#2e7d32"
                                        fill="#81c784"
                                    />

                                </AreaChart>

                            </ResponsiveContainer>

                        </CardContent>

                    </Card>

                </Grid>

                {/* Pie */}

                <Grid
                    size={{ xs: 12, md: 4 }}
                    component={motion.div}
                    initial={{
                        opacity: 0,
                        x: 50,
                    }}
                    animate={{
                        opacity: 1,
                        x: 0,
                    }}
                    transition={{
                        duration: 0.6,
                        delay: 0.75,
                        easing: "ease-out",
                    }}>

                    <Card
                        component={motion.div}
                        whileHover={{
                            y: -6,
                            scale: 1.02,
                        }}
                        transition={{
                            duration: 0.2,
                        }}>

                        <CardContent>

                            <Typography
                                variant="h6"
                                gutterBottom>

                                Estado de abonos

                            </Typography>
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        data={pie}
                                        dataKey="value"
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={70}
                                        outerRadius={100}
                                        label
                                    >
                                        {pie.map((entry, index) => (
                                            <Cell key={index} fill={COLORS[index]} />
                                        ))}
                                    </Pie>

                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>

                        </CardContent>

                    </Card>

                </Grid>

                {/* Barras */}

                <Grid size={12}
                    component={motion.div}
                    initial={{
                        opacity: 0,
                        y: 40,
                    }}
                    animate={{
                        opacity: 1,
                        y: 0,
                    }}
                    transition={{
                        duration: 0.6,
                        delay: 0.85,
                        easing: "ease-out",
                    }}>

                    <Card
                        component={motion.div}
                        whileHover={{
                            y: -6,
                            scale: 1.02,
                        }}
                        transition={{
                            duration: 0.2,
                        }}>

                        <CardContent>

                            <Typography
                                variant="h6"
                                gutterBottom>

                                Abonos programados

                            </Typography>

                            <ResponsiveContainer
                                width="100%"
                                height={300}>

                                <BarChart data={datos}>

                                    <CartesianGrid strokeDasharray="3 3" />

                                    <XAxis dataKey="fecha" />

                                    <YAxis />

                                    <Tooltip />

                                    <Bar dataKey="cantidad">

                                        {

                                            datos.map((item, index) => (

                                                <Cell
                                                    key={index}
                                                    fill={
                                                        item.ahorro_pagado
                                                            ? "#4caf50"
                                                            : "#ef5350"
                                                    }
                                                />

                                            ))

                                        }

                                    </Bar>

                                </BarChart>

                            </ResponsiveContainer>

                        </CardContent>

                    </Card>

                </Grid>

                {/* Tabla */}

                <Grid
                    size={12}
                    component={motion.div}
                    initial={{
                        opacity: 0,
                        y: 40,
                    }}
                    animate={{
                        opacity: 1,
                        y: 0,
                    }}
                    transition={{
                        duration: 0.6,
                        delay: 0.95,
                        easing: "ease-out",
                    }}>

                    <Card
                        component={motion.div}
                        whileHover={{
                            y: -6,
                            scale: 1.02,
                        }}
                        transition={{
                            duration: 0.2,
                        }}>

                        <CardContent>

                            <Typography
                                variant="h6"
                                gutterBottom>

                                Seguimiento de abonos

                            </Typography>

                            <TableContainer
                                component={Paper}>

                                <Table>

                                    <TableHead>

                                        <TableRow>

                                            <TableCell>

                                                Fecha

                                            </TableCell>

                                            <TableCell>

                                                Cantidad

                                            </TableCell>

                                            <TableCell>

                                                Estado

                                            </TableCell>

                                        </TableRow>

                                    </TableHead>

                                    <TableBody>

                                        {

                                            datos.map(item => (

                                                <TableRow
                                                    key={item.id_ahorro}>

                                                    <TableCell>

                                                        {item.fecha}

                                                    </TableCell>

                                                    <TableCell>

                                                        ${item.cantidad.toLocaleString("en-US")}

                                                    </TableCell>

                                                    <TableCell>

                                                        <Chip
                                                            label={
                                                                item.ahorro_pagado
                                                                    ? "Pagado"
                                                                    : "Pendiente"
                                                            }
                                                            color={
                                                                item.ahorro_pagado
                                                                    ? "success"
                                                                    : "error"
                                                            }
                                                        />

                                                    </TableCell>

                                                </TableRow>

                                            ))

                                        }

                                    </TableBody>

                                </Table>

                            </TableContainer>

                        </CardContent>

                    </Card>

                </Grid>

            </Grid>
        </Box>
    );

};

export default GraficosSeguimientos;